import { CameraOutlined, DeleteOutlined, PictureOutlined } from '@ant-design/icons';
import { Button, Form, Input, Modal, Switch, Upload } from 'antd';
import type { UploadChangeParam } from 'antd/es/upload';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { PreviewImage } from '@/components/PreviewImage/PreviewImage';
import { BRAND } from '@/constants/brand';
import { useNotify } from '@/hooks/useNotify';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useMutationReset } from '@/hooks/useMutationReset';
import {
  useCreateBrandMutation,
  useUpdateBrandMutation,
  useUploadBrandImageMutation,
} from '@/modules/brands/brandsApi';
import type { Brand } from '@/types/brand';
import { resolveMutationResult } from '@/utils/apiResponse';
import { getBrandImageUrl } from '@/utils/media';
import styles from './BrandFormModal.module.scss';

interface BrandFormModalProps {
  open: boolean;
  brand?: Brand | null;
  onClose: () => void;
}

interface BrandFormValues {
  name: string;
  isActive: boolean;
}

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
const MAX_SIZE_BYTES = 5 * 1024 * 1024;

export function BrandFormModal({ open, brand, onClose }: BrandFormModalProps) {
  const notify = useNotify();
  const isEdit = Boolean(brand);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>();
  const [createBrand, createState] = useCreateBrandMutation();
  const [updateBrand, updateState] = useUpdateBrandMutation();
  const [uploadBrandImage, uploadState] = useUploadBrandImageMutation();
  const isLoading = createState.isLoading || updateState.isLoading || uploadState.isLoading;
  useGlobalLoader(isLoading);

  const { control, handleSubmit, reset } = useForm<BrandFormValues>({
    defaultValues: {
      name: '',
      isActive: true,
    },
  });

  useMutationReset(createState.reset);
  useMutationReset(updateState.reset);
  useMutationReset(uploadState.reset);

  const existingImageUrl = useMemo(
    () => getBrandImageUrl(brand?.image),
    [brand?.image],
  );

  useEffect(() => {
    if (!open) return;

    reset({
      name: brand?.name ?? '',
      isActive: brand?.isActive ?? true,
    });
    setImageFile(null);
    setPreviewUrl(existingImageUrl);
  }, [brand, existingImageUrl, open, reset]);

  const handleImageChange = (info: UploadChangeParam) => {
    const file = info.file.originFileObj ?? info.file;
    if (!file || !(file instanceof File)) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      notify.error('Please upload a PNG, JPG, WEBP, or GIF image.');
      return;
    }

    if (file.size > MAX_SIZE_BYTES) {
      notify.error('Image must be 5MB or smaller.');
      return;
    }

    setImageFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setPreviewUrl(undefined);
  };

  const uploadImageIfNeeded = async (): Promise<string | undefined> => {
    if (!imageFile) {
      return brand?.image;
    }

    const formData = new FormData();
    formData.append('image', imageFile);
    const result = await resolveMutationResult<{ image: string }>(uploadBrandImage(formData));

    if (!result.ok) {
      notify.error(result.message);
      return undefined;
    }

    return result.data.image;
  };

  const onSubmit = async (values: BrandFormValues) => {
    const image = await uploadImageIfNeeded();
    if (!image) {
      if (!isEdit) {
        notify.error('Brand image is required.');
      }
      return;
    }

    const payload = {
      name: values.name.trim(),
      image,
      isActive: values.isActive,
    };

    if (isEdit) {
      if (!brand?.id) return;

      const result = await resolveMutationResult(updateBrand({ id: brand.id, body: payload }));
      if (result.ok) {
        notify.success(result.message ?? `Brand ${isEdit ? 'updated' : 'created'} successfully.`);
        onClose();
        return;
      }

      notify.error(result.message);
      return;
    }

    const result = await resolveMutationResult(createBrand(payload));

    if (result.ok) {
      notify.success(result.message ?? `Brand ${isEdit ? 'updated' : 'created'} successfully.`);
      onClose();
      return;
    }

    notify.error(result.message);
  };

  return (
    <Modal
      title={isEdit ? 'Edit brand' : 'Add brand'}
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnHidden
      width={480}
    >
      <Form layout="vertical" className={styles.form} onFinish={handleSubmit(onSubmit)}>
        <Controller
          name="name"
          control={control}
          rules={{ required: 'Name is required' }}
          render={({ field, fieldState }) => (
            <Form.Item
              label="Name"
              required
              validateStatus={fieldState.error ? 'error' : ''}
              help={fieldState.error?.message}
            >
              <Input {...field} placeholder="Brand name" />
            </Form.Item>
          )}
        />

        <Form.Item label="Image" required={!isEdit} className={styles.imageField}>
          <div className={styles.imagePicker}>
            <Upload
              accept={ACCEPTED_TYPES.join(',')}
              showUploadList={false}
              beforeUpload={() => false}
              onChange={handleImageChange}
              disabled={isLoading}
            >
              <button type="button" className={styles.imageThumb}>
                {previewUrl ? (
                  <PreviewImage
                    src={previewUrl}
                    alt="Brand preview"
                    fill
                    className={styles.imagePreview}
                    stopPropagation
                  />
                ) : (
                  <span className={styles.imagePlaceholder}>
                    <PictureOutlined />
                  </span>
                )}
                <span className={styles.imageOverlay}>
                  <CameraOutlined />
                </span>
              </button>
            </Upload>

            <div className={styles.imageMeta}>
              <p className={styles.imageTitle}>
                {previewUrl ? 'Brand image selected' : 'Upload brand image'}
              </p>
              <p className={styles.imageHint}>PNG, JPG, WEBP or GIF · Max 5MB</p>
              {previewUrl ? (
                <div className={styles.imageActions}>
                  <Upload
                    accept={ACCEPTED_TYPES.join(',')}
                    showUploadList={false}
                    beforeUpload={() => false}
                    onChange={handleImageChange}
                    disabled={isLoading}
                  >
                    <Button size="small" type="default">
                      Change
                    </Button>
                  </Upload>
                  <Button
                    size="small"
                    danger
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={handleRemoveImage}
                    disabled={isLoading}
                  >
                    Remove
                  </Button>
                </div>
              ) : null}
            </div>
          </div>
        </Form.Item>

        <Controller
          name="isActive"
          control={control}
          render={({ field }) => (
            <Form.Item label="Status" className={styles.statusField}>
              <Switch
                checked={field.value}
                onChange={field.onChange}
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                style={{ background: field.value ? BRAND.primary : undefined }}
              />
            </Form.Item>
          )}
        />

        <div className={styles.actions}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            {isEdit ? 'Save changes' : 'Create brand'}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
